import React, { useState, useEffect } from 'react';

interface SettingsViewProps {
  brand: string;
}

type Tab = 'brand' | 'voice' | 'quality' | 'sources' | 'gsc' | 'carousel-gates';
const TAB_LABELS: Record<Tab, string> = { brand: 'Brand', voice: 'Voice', quality: 'Quality', sources: 'Sources', gsc: 'GSC / Analytics', 'carousel-gates': 'Carousel Gates' };

export default function SettingsView({ brand }: SettingsViewProps) {
  const [config, setConfig] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<Tab>('brand');
  const [gscForm, setGscForm] = useState<Record<string, string>>({});
  const [saveMsg, setSaveMsg] = useState('');
  // Founder voice + tone editor. Array fields are edited as newline-separated text.
  const [voiceForm, setVoiceForm] = useState<Record<string, string>>({});
  const [voiceMsg, setVoiceMsg] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractMsg, setExtractMsg] = useState('');
  // #177 carousel-gate config
  const [personasForm, setPersonasForm] = useState<Record<string, { avoid?: string[]; use?: string[] }>>({});
  const [featuresForm, setFeaturesForm] = useState<Array<{ name: string; status: string }>>([]);
  const [newPersonaName, setNewPersonaName] = useState('');
  const [personasMsg, setPersonasMsg] = useState('');
  const [featuresMsg, setFeaturesMsg] = useState('');

  useEffect(() => {
    fetch(`/api/${brand}/config`)
      .then((r) => r.json())
      .then(setConfig)
      .catch(() => setConfig(null));
  }, [brand]);

  // Seed the editable GSC form once config arrives.
  useEffect(() => {
    const g = config?.gsc;
    if (g) {
      setGscForm({
        siteUrl: g.siteUrl ?? '',
        serviceAccountJson: g.serviceAccountJson ?? '',
        ga4PropertyId: g.ga4PropertyId ?? '',
      });
    }
  }, [config]);

  // Seed the editable voice form once config arrives (arrays → newline text).
  useEffect(() => {
    const v = config?.voice;
    if (v) setVoiceForm({
      voice: v.voice ?? '',
      personaIdentity: v.persona?.identity ?? '',
      personaTraits: (v.persona?.traits ?? []).join('\n'),
      personaAntiTraits: (v.persona?.anti_traits ?? []).join('\n'),
      principles: (v.principles ?? []).join('\n'),
      lexiconUse: (v.mandatory_lexicon?.use ?? v.lexicon?.use ?? []).join('\n'),
      // Merge the legacy hard_avoidance list with mandatory_lexicon.never (dedup)
      // so the founder sees the complete banned-word list — not just one of them.
      lexiconNever: [...new Set([
        ...(v.mandatory_lexicon?.never ?? v.lexicon?.never ?? []),
        ...(v.hard_avoidance ?? []),
      ])].join('\n'),
    });
  }, [config]);

  // Seed the carousel-gate forms once config arrives (#177).
  useEffect(() => {
    setPersonasForm((config?.personaMatrix?.personas as Record<string, { avoid?: string[]; use?: string[] }>) ?? {});
    setFeaturesForm((config?.featureRegistry?.features as Array<{ name: string; status: string }>) ?? []);
  }, [config]);

  if (!config) return <div className="view-page"><div style={{ color: 'var(--text-tertiary)' }}>Loading settings...</div></div>;

  const tabs: Tab[] = ['brand', 'voice', 'quality', 'sources', 'gsc', 'carousel-gates'];
  const brandCfg = config.brand || {};
  const gscCfg = config.gsc || {};

  async function saveGsc() {
    setSaveMsg('Saving…');
    try {
      const r = await fetch(`/api/${brand}/gsc/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gscForm),
      });
      const data = await r.json();
      if (data?.ok) {
        setSaveMsg('Saved ✓');
        setConfig((prev: any) => ({ ...prev, gsc: data.gsc }));
        setTimeout(() => setSaveMsg(''), 3000);
      } else setSaveMsg(`Error: ${data?.error ?? 'save failed'}`);
    } catch (e) {
      setSaveMsg(`Error: ${(e as Error).message}`);
    }
  }

  // Upload a writing sample (.md/.txt/.docx) → LLM extracts the voice → pre-fills
  // the form below. The founder reviews + clicks Save (this does not save itself).
  async function onVoiceFile(file: File) {
    if (file.size > 2 * 1024 * 1024) { setExtractMsg('Error: file too large (max 2MB)'); return; }
    setExtractMsg('Reading sample…');
    setExtracting(true);
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result));
        fr.onerror = () => rej(new Error('could not read file'));
        fr.readAsDataURL(file);
      });
      const contentBase64 = dataUrl.split(',')[1] ?? '';
      setExtractMsg('Extracting voice…');
      const r = await fetch(`/api/${brand}/voice/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, contentBase64 }),
      });
      const data = await r.json();
      if (data?.ok && data.fields) {
        const f = data.fields;
        setVoiceForm({
          voice: f.voice ?? '',
          personaIdentity: f.personaIdentity ?? '',
          personaTraits: (f.personaTraits ?? []).join('\n'),
          personaAntiTraits: (f.personaAntiTraits ?? []).join('\n'),
          principles: (f.principles ?? []).join('\n'),
          lexiconUse: (f.lexiconUse ?? []).join('\n'),
          lexiconNever: (f.lexiconNever ?? []).join('\n'),
        });
        setExtractMsg('Extracted ✓ — review the fields below, then Save voice');
      } else setExtractMsg(`Error: ${data?.error ?? 'extraction failed'}`);
    } catch (e) {
      setExtractMsg(`Error: ${(e as Error).message}`);
    } finally {
      setExtracting(false);
    }
  }

  async function saveVoice() {
    setVoiceMsg('Saving…');
    try {
      const r = await fetch(`/api/${brand}/voice/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(voiceForm),
      });
      const data = await r.json();
      if (data?.ok) {
        setVoiceMsg('Saved ✓');
        setConfig((prev: any) => ({ ...prev, voice: data.voice }));
        setTimeout(() => setVoiceMsg(''), 3000);
      } else setVoiceMsg(`Error: ${data?.error ?? 'save failed'}`);
    } catch (e) {
      setVoiceMsg(`Error: ${(e as Error).message}`);
    }
  }

  async function savePersonaMatrix() {
    setPersonasMsg('Saving…');
    try {
      const r = await fetch(`/api/${brand}/persona-matrix/settings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personas: personasForm }),
      });
      const data = await r.json();
      if (data?.ok) {
        setPersonasMsg('Saved ✓');
        setConfig((prev: any) => ({ ...prev, personaMatrix: data.personaMatrix }));
        setTimeout(() => setPersonasMsg(''), 3000);
      } else setPersonasMsg(`Error: ${data?.error ?? 'save failed'}`);
    } catch (e) { setPersonasMsg(`Error: ${(e as Error).message}`); }
  }

  async function saveFeatureRegistry() {
    setFeaturesMsg('Saving…');
    try {
      const r = await fetch(`/api/${brand}/feature-registry/settings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ features: featuresForm }),
      });
      const data = await r.json();
      if (data?.ok) {
        setFeaturesMsg('Saved ✓');
        setConfig((prev: any) => ({ ...prev, featureRegistry: data.featureRegistry }));
        setTimeout(() => setFeaturesMsg(''), 3000);
      } else setFeaturesMsg(`Error: ${data?.error ?? 'save failed'}`);
    } catch (e) { setFeaturesMsg(`Error: ${(e as Error).message}`); }
  }

  const gscField = (key: string, label: string, placeholder: string, type = 'text') => (
    <div className="settings-group">
      <div className="settings-label">{label}</div>
      <input
        type={type}
        value={gscForm[key] ?? ''}
        placeholder={placeholder}
        onChange={(e) => setGscForm({ ...gscForm, [key]: e.target.value })}
        style={{ width: '100%', padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13 }}
      />
    </div>
  );

  const fieldStyle = { width: '100%', padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit' as const };
  const voiceField = (key: string, label: string, placeholder: string, multiline: boolean, rows = 3) => (
    <div className="settings-group">
      <div className="settings-label">{label}</div>
      {multiline ? (
        <textarea
          value={voiceForm[key] ?? ''}
          placeholder={placeholder}
          rows={rows}
          onChange={(e) => setVoiceForm({ ...voiceForm, [key]: e.target.value })}
          style={{ ...fieldStyle, resize: 'vertical' }}
        />
      ) : (
        <input
          type="text"
          value={voiceForm[key] ?? ''}
          placeholder={placeholder}
          onChange={(e) => setVoiceForm({ ...voiceForm, [key]: e.target.value })}
          style={fieldStyle}
        />
      )}
    </div>
  );

  return (
    <div className="view-page">
      <h2 className="view-page__title">Settings</h2>

      <div className="settings-tabs">
        {tabs.map((t) => (
          <button key={t} className={`settings-tab ${activeTab === t ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab(t)}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="settings-content">
        {activeTab === 'brand' && (
          <div>
            <div className="settings-group">
              <div className="settings-label">Company</div>
              <div className="settings-value">{brandCfg.company?.name || brand}</div>
              {brandCfg.company?.tagline && <div className="settings-value settings-value--dim">{brandCfg.company.tagline}</div>}
              {brandCfg.company?.website && <div className="settings-value settings-value--dim">{brandCfg.company.website}</div>}
            </div>
            {brandCfg.colors && (
              <div className="settings-group">
                <div className="settings-label">Colors</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {Object.entries(brandCfg.colors).map(([name, hex]) => (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 16, height: 16, borderRadius: 0, background: hex as string, border: '1px solid var(--border)' }} />
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="settings-hint">Edit: <code>brands/{brand}/brand.yaml</code></div>
          </div>
        )}

        {activeTab === 'voice' && (
          <div>
            <div className="settings-value settings-value--dim" style={{ marginBottom: 12 }}>
              Set the voice + tone the engine writes in for this brand. For a founder brand, this is <em>your</em> voice — what you sound like, how you frame things, words to use and avoid. Saved to <code>brands/{brand}/voice.yaml</code> and applied to every generated post. List fields are one item per line.
            </div>
            <div className="settings-group" style={{ paddingBottom: 14, marginBottom: 6, borderBottom: '1px solid var(--border)' }}>
              <div className="settings-label">Upload a writing sample (.md, .txt, .docx)</div>
              <div className="settings-value settings-value--dim" style={{ marginBottom: 6 }}>
                Optional shortcut — the engine reads your sample and fills the fields below in your voice. Review them, then Save.
              </div>
              <input
                type="file"
                accept=".md,.markdown,.txt,.docx"
                disabled={extracting}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void onVoiceFile(f); e.currentTarget.value = ''; }}
                style={{ fontSize: 13 }}
              />
              {extractMsg && <span style={{ marginLeft: 10, fontSize: 12, color: extractMsg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)' }}>{extractMsg}</span>}
            </div>
            {voiceField('voice', 'Voice (one-line descriptor)', 'e.g. Founder who builds — direct, specific, shows the work', false)}
            {voiceField('personaIdentity', 'Persona — who is writing', 'e.g. The founder of ZopDev, an engineer who ships and talks about the product first-hand.', true, 2)}
            {voiceField('personaTraits', 'Voice traits (one per line)', 'direct and specific\nquantifies outcomes\nshows real code', true, 4)}
            {voiceField('personaAntiTraits', 'Anti-traits — avoid sounding like (one per line)', 'vague consultant-speak\ngeneric advice\nhype', true, 3)}
            {voiceField('principles', 'Writing principles (one per line)', 'lead with the mechanism\nevery claim needs a number', true, 4)}
            {voiceField('lexiconUse', 'Words / phrases to use (one per line)', 'we built\nin production\nmeasured', true, 3)}
            {voiceField('lexiconNever', 'Words to never use (one per line)', 'leverage\nsynergy\ngame-changing', true, 3)}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
              <button className="settings-tab settings-tab--active" onClick={saveVoice} style={{ cursor: 'pointer' }}>Save voice</button>
              {voiceMsg && <span style={{ fontSize: 12, color: voiceMsg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)' }}>{voiceMsg}</span>}
            </div>
          </div>
        )}

        {activeTab === 'quality' && (
          <div>
            <div className="settings-group">
              <div className="settings-label">Quality thresholds are configured per format</div>
              <div className="settings-value settings-value--dim">Blog, Social, Newsletter, and Ebook each have their own word count, reading level, and content density targets.</div>
            </div>
            <div className="settings-hint">Edit: <code>quality-thresholds.yaml</code></div>
          </div>
        )}

        {activeTab === 'sources' && (
          <div>
            <div className="settings-group">
              <div className="settings-label">Knowledge base sync</div>
              <div className="settings-value settings-value--dim">Configure RSS feeds, Notion databases, Google Drive folders, and local directories as content sources.</div>
            </div>
            <div className="settings-hint">Edit: <code>brands/{brand}/sources.yaml</code></div>
          </div>
        )}

        {activeTab === 'gsc' && (
          <div>
            <div className="settings-value settings-value--dim" style={{ marginBottom: 12 }}>
              GSC-driven SEO engine. The Sheets ingest works without creds; the service account unlocks the live Search Analytics lanes (API sync, cannibalization). Indexing and attribution are not wired up yet — these creds are stored ahead of that.
            </div>
            {gscField('siteUrl', 'GSC property / site URL', 'https://zop.dev/')}
            {gscField('serviceAccountJson', 'Service-account JSON path (Search Analytics + URL Inspection)', '/path/to/service-account.json')}
            {gscField('ga4PropertyId', 'GA4 property id (attribution)', '123456789')}
            <div className="settings-group">
              <div className="settings-label">PostHog project key (attribution)</div>
              <div className="settings-value settings-value--dim">
                Env-only secret: set <code>POSTHOG_PROJECT_KEY</code> in <code>blog-publisher/.env</code>. Not stored in <code>gsc.yaml</code> (git-tracked).
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
              <button className="settings-tab settings-tab--active" onClick={saveGsc} style={{ cursor: 'pointer' }}>Save</button>
              {saveMsg && <span style={{ fontSize: 12, color: saveMsg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)' }}>{saveMsg}</span>}
            </div>
            <div className="settings-group" style={{ marginTop: 12 }}>
              <div className="settings-label">Status</div>
              <div className="settings-value settings-value--dim">Sheets ingest: {gscCfg.hasSheets ? 'configured ✓' : 'not configured'}</div>
              <div className="settings-value settings-value--dim">Service account: {gscCfg.hasServiceAccount ? 'set ✓' : 'unset — Search Console API blocked'}</div>
              <div className="settings-value settings-value--dim">PostHog key (env): {gscCfg.hasPosthog ? 'set ✓' : 'unset — traffic attribution off'}</div>
            </div>
            <div className="settings-hint">Also editable via <code>bun run orchestrate.ts gsc-config {brand}</code> or <code>brands/{brand}/gsc.yaml</code></div>
          </div>
        )}

        {activeTab === 'carousel-gates' && (
          <div>
            <div className="settings-value settings-value--dim" style={{ marginBottom: 12 }}>
              Product config for the carousel pre-generation gates (#172/#177). Both are optional — when empty, the gate no-ops (passes). Editing here writes <code>brands/{brand}/persona-matrix.yaml</code> and <code>feature-registry.yaml</code>.
            </div>

            {/* ICP language — persona matrix */}
            <div className="settings-group" style={{ marginBottom: 20 }}>
              <div className="settings-label">ICP language — avoid words per persona</div>
              <div className="settings-value settings-value--dim" style={{ marginBottom: 8 }}>
                Carousel generation warns when any avoid-word appears in the narrative.
              </div>
              {Object.entries(personasForm).map(([name, persona]) => (
                <div key={name} style={{ marginBottom: 10, padding: 8, borderRadius: 0, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{name}</span>
                    <button onClick={() => { const n = { ...personasForm }; delete n[name]; setPersonasForm(n); }} style={{ fontSize: 11, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2 }}>Avoid (comma-separated)</div>
                  <input type="text" value={(persona.avoid ?? []).join(', ')} placeholder="leverage, synergy, best-in-class"
                    onChange={(e) => setPersonasForm({ ...personasForm, [name]: { ...persona, avoid: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }} />
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2 }}>Use (comma-separated, optional)</div>
                  <input type="text" value={(persona.use ?? []).join(', ')} placeholder="cost discipline, verified ROI"
                    onChange={(e) => setPersonasForm({ ...personasForm, [name]: { ...persona, use: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13 }} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <input type="text" value={newPersonaName} placeholder="New persona name (e.g. finops_cfo)"
                  onChange={(e) => setNewPersonaName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const n = newPersonaName.trim(); if (n && !personasForm[n]) { setPersonasForm({ ...personasForm, [n]: { avoid: [], use: [] } }); setNewPersonaName(''); } } }}
                  style={{ flex: 1, padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13 }} />
                <button onClick={() => { const n = newPersonaName.trim(); if (n && !personasForm[n]) { setPersonasForm({ ...personasForm, [n]: { avoid: [], use: [] } }); setNewPersonaName(''); } }}
                  style={{ fontSize: 12, padding: '5px 10px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}>+ Add persona</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <button className="settings-tab settings-tab--active" onClick={savePersonaMatrix} style={{ cursor: 'pointer' }}>Save persona matrix</button>
                {personasMsg && <span style={{ fontSize: 12, color: personasMsg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)' }}>{personasMsg}</span>}
              </div>
            </div>

            {/* Shipped-claim — feature registry */}
            <div className="settings-group">
              <div className="settings-label">Feature registry — shipped status</div>
              <div className="settings-value settings-value--dim" style={{ marginBottom: 8 }}>
                Carousel generation hard-blocks if a non-shipped feature is presented as live.
              </div>
              {featuresForm.map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <input type="text" value={f.name} placeholder="Feature name"
                    onChange={(e) => { const n = [...featuresForm]; n[i] = { ...n[i], name: e.target.value }; setFeaturesForm(n); }}
                    style={{ flex: 1, padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13 }} />
                  <select value={f.status} onChange={(e) => { const n = [...featuresForm]; n[i] = { ...n[i], status: e.target.value }; setFeaturesForm(n); }}
                    style={{ padding: '6px 8px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: 13 }}>
                    <option value="shipped">shipped</option>
                    <option value="partial">partial</option>
                    <option value="planned">planned</option>
                    <option value="internal">internal</option>
                  </select>
                  <button onClick={() => setFeaturesForm(featuresForm.filter((_, j) => j !== i))} style={{ fontSize: 11, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
                </div>
              ))}
              <button onClick={() => setFeaturesForm([...featuresForm, { name: '', status: 'shipped' }])}
                style={{ fontSize: 12, padding: '5px 10px', borderRadius: 0, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}>+ Add feature</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <button className="settings-tab settings-tab--active" onClick={saveFeatureRegistry} style={{ cursor: 'pointer' }}>Save feature registry</button>
                {featuresMsg && <span style={{ fontSize: 12, color: featuresMsg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)' }}>{featuresMsg}</span>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
